import { Fragment } from "react";
import { Alert, AlertIcon, Box, Divider, HStack, Image, SimpleGrid, Text } from "@chakra-ui/react";
import { LocationType } from "@tether-web-portal/api/properties/hooks";
import { RoomLocationWithReading } from "@tether-web-portal/types/RoomLocation";
import dayjs from "@tether-web-portal/dayjs-setup";
import { DATE_FORMATS } from "@tether-web-portal/constants/dateFormats";
import { PrimaryButton } from "@tether-web-portal/components/button/PrimaryButton";
import { GatewayLinkedDevices } from "@tether-web-portal/components/areas/location-reading/GatewayLinkedDevices";
import { DetailGridItem } from "./DetailGridItem";
import {
  SampleBreakdown,
  SampleReadingBreakdownField,
} from "@tether-web-portal/components/areas/location-reading/makeSampleBreakdown";
import { OutsideWithReading } from "@tether-web-portal/types/OutsideWithReading";
import { useNavigate } from "react-router-dom";
import { DeviceType } from "@tether-web-portal/types/DeviceType";
import { ChildRoomLocationsMap } from "./useChildRoomLocations";
import { formatDeviceName } from "@tether-web-portal/utils/formatDeviceName";
import numeral from "numeral";
import { DeviceReadingModalItem } from "@tether-web-portal/components/areas/area-device-reading-item/DeviceReadingModalItem";

const readingBreakdownFields = [
  SampleReadingBreakdownField.co2,
  SampleReadingBreakdownField.temp,
  SampleReadingBreakdownField.occupied,
  SampleReadingBreakdownField.humidity,
  SampleReadingBreakdownField.sound,
  SampleReadingBreakdownField.light,
  SampleReadingBreakdownField.pressure,
  SampleReadingBreakdownField.pm1,
  SampleReadingBreakdownField.pm2_5,
  SampleReadingBreakdownField.pm10,
  SampleReadingBreakdownField.minTVOC,
  SampleReadingBreakdownField.tvoc,
  SampleReadingBreakdownField.maxTVOC,
  SampleReadingBreakdownField.flowRate,
  SampleReadingBreakdownField.temperatureInlet,
  SampleReadingBreakdownField.temperatureOutlet,
] as const;

interface DetailsViewProps {
  roomLocation: RoomLocationWithReading | OutsideWithReading;
  breakdown?: SampleBreakdown;
  locationType: "ROOMLOCATION" | "OUTSIDE";
  hasDevice: boolean;
  propertyId: string;
  onChildDeviceTypeClick: (deviceType: DeviceType) => void;
  childRoomLocationMap: ChildRoomLocationsMap;
}

export const DetailsView = ({
  roomLocation,
  breakdown,
  locationType,
  hasDevice,
  propertyId,
  onChildDeviceTypeClick,
  childRoomLocationMap,
}: DetailsViewProps) => {
  const lastSeenAt = roomLocation.device?.lastSample?.timestamp;
  const serialNumber = roomLocation.device?.serialNumber;
  const sku = roomLocation.device?.sku;

  const navigate = useNavigate();
  return (
    <>
      {Boolean(roomLocation.image) && (
        <Image
          borderRadius={8}
          onClick={() => window.open(roomLocation.image, "_blank")}
          cursor="pointer"
          alt={`${roomLocation.name} Image`}
          src={roomLocation.image}
          objectFit="cover"
          height="242px"
          width="100%"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}

      {Boolean(breakdown && !breakdown.isOnline) && (
        <Alert status="warning" mt={5}>
          <HStack>
            <AlertIcon />
            <Box>Device is offline</Box>
            {Boolean(lastSeenAt) && (
              <Box>
                {" "}
                Last Seen: {dayjs(lastSeenAt!).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME_12_HOUR_SLASHED)}
              </Box>
            )}
          </HStack>
        </Alert>
      )}

      {Boolean(breakdown?.healthyBreakdownText && !breakdown.isReallyOld) && (
        <Text fontSize={16} mt={6} mb={6} textAlign="center">
          {breakdown?.healthyBreakdownText}
        </Text>
      )}

      <Box width="100%" mb={"32px"}>
        {Boolean(breakdown?.isReallyOld === false) && (
          <>
            <SimpleGrid minChildWidth={"100px"} spacing={2}>
              {readingBreakdownFields.map((key: SampleReadingBreakdownField) => {
                const { [key]: readingBreakdown } = breakdown || {};
                if (readingBreakdown === undefined) return <Fragment key={key} />;
                return <DeviceReadingModalItem key={key} {...readingBreakdown} />;
              })}
            </SimpleGrid>
            <Divider />
          </>
        )}

        <DetailGridItem label="Device">{formatDeviceName(roomLocation.device?.type)}</DetailGridItem>

        <Divider />

        <DetailGridItem label="Last seen">
          {lastSeenAt ? dayjs(lastSeenAt!).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME_12_HOUR_SLASHED) : "Never"}
        </DetailGridItem>

        <Divider />

        <DetailGridItem label="Battery Level">
          {breakdown?.battery !== undefined ? numeral(breakdown!.battery).format("0%") : "N/A"}
        </DetailGridItem>

        <Divider />

        <DetailGridItem label="Serial">{serialNumber ? serialNumber : "N/A"}</DetailGridItem>

        <Divider />

        <DetailGridItem label="SKU">{sku ? sku : "N/A"}</DetailGridItem>

        {(childRoomLocationMap[DeviceType.ROOM_RADAR_LINE] ?? []).length > 0 && (
          <>
            <Divider />
            <DetailGridItem
              label="Entry/Exit Lines"
              tooltip="These lines track people entering or leaving this area"
              onClick={() => onChildDeviceTypeClick(DeviceType.ROOM_RADAR_LINE)}
            >
              {childRoomLocationMap[DeviceType.ROOM_RADAR_LINE]!.length}
            </DetailGridItem>
          </>
        )}

        {(childRoomLocationMap[DeviceType.ROOM_RADAR_REGION] ?? []).length > 0 && (
          <>
            <Divider />
            <DetailGridItem
              label="Regions"
              tooltip="Specific spaces within this area that are tracking people"
              onClick={() => onChildDeviceTypeClick(DeviceType.ROOM_RADAR_REGION)}
            >
              {childRoomLocationMap[DeviceType.ROOM_RADAR_REGION]!.length}
            </DetailGridItem>
          </>
        )}
      </Box>

      {Boolean(roomLocation.device?.type === "GATEWAY") && (
        <GatewayLinkedDevices device={roomLocation.device} installationId={propertyId} />
      )}

      {!hasDevice && (
        <Text p={5} pt={0} pb={8}>
          No device found for this area, use the mobile app to configure a device.
        </Text>
      )}

      <Box>
        {Boolean(
          window.location.pathname !== `/area/outside/${roomLocation.id}` &&
            locationType === LocationType.RoomLocation,
        ) && (
          <PrimaryButton
            onClick={() => navigate(`/area/room/${(roomLocation as RoomLocationWithReading).roomId}`)}
            text="View Room"
          />
        )}
        {Boolean(
          window.location.pathname !== `/area/outside/${roomLocation.id}` &&
            locationType === LocationType.Outside,
        ) && (
          <PrimaryButton
            onClick={() => navigate(`/area/outside/${roomLocation.id}`)}
            text="View Outside Area"
          />
        )}
      </Box>
    </>
  );
};
